import React, { ReactNode } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import Checkbox from '../Checkbox/Checkbox.js';
import { IPillProps } from '../Pill/models/IPillProps.js';
import Translate from '../Translate/Translate.js';

import { IButtonProps } from './components/Button/models/index.js';
import Dropdown from './components/Dropdown/index.js';
import DropdownItem from './components/DropdownItem/index.js';
import { IDropdownItemProps } from './components/DropdownItem/models/index.js';
import { EContextMenuDisplayTypes, TContextMenuDisplayTypes } from './models/EContextMenuDisplayTypes.js';
import { EContextMenuTypes, TContextMenuTypes } from './models/EContextMenuTypes.js';
import {
	IContextMenuFilterMappingItem,
	TContextMenuFilterMapping,
	TContextMenuFilterMappingSubItem,
} from './models/IContextMenuFilterMapping';
import { IContextMenuAction, IContextMenuConfiguration, IContextMenuProps, IContextMenuState } from './models/index.js';
import { THandleKeyEvent } from './models/THandleKeyEvent.js';
import { THandleOnClickEvent } from './models/THandleOnClickEvent.js';
import ActionLabel from './styledComponents/ActionLabel.js';
import ClearButton from './styledComponents/ClearButton.js';
import ContextMenuPill from './styledComponents/ContextMenuPill.js';
import Count from './styledComponents/Count.js';
import DropdownContent from './styledComponents/DropdownContent.js';
import DropdownItemWrapper from './styledComponents/DropdownItemWrapper.js';
import Icon from './styledComponents/Icon.js';
import IconWrapper from './styledComponents/IconWrapper.js';
import InlineDescription from './styledComponents/InlineDescription.js';
import InlineItemWrapper from './styledComponents/InlineItemWrapper.js';
import InlineLabel from './styledComponents/InlineLabel.js';
import StyledLabel from './styledComponents/StyledLabel.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CONTEXT_MENU_CATEGORY_KEY, CONTEXT_MENU_COMPONENT_KEY } from './ContextMenu.meta.js';

const NAVIGATION_KEYS = [
	KEYS.UP_ARROW,
	KEYS.DOWN_ARROW,
	KEYS.LEFT_ARROW,
	KEYS.RIGHT_ARROW,
	KEYS.PAGE_UP,
	KEYS.PAGE_DOWN,
	KEYS.HOME,
	KEYS.END,
];

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

const EXIT_KEYS = [KEYS.ESCAPE];

export class ContextMenu extends ComponentBase<IContextMenuProps, IContextMenuState, IContextMenuConfiguration> {
	categoryKey = CONTEXT_MENU_CATEGORY_KEY;
	componentKey = CONTEXT_MENU_COMPONENT_KEY;

	activeMenuType: 'menu' | 'sub-menu' = 'menu';
	subItemDropdownTopMargin = 0;
	itemReferences: HTMLElement[];
	subItemReferences: HTMLElement[] = [];
	internalFocusedIndex = 0;
	internalFocusedSubIndex = 0;
	forceInternalFocus = false;
	focusedButton = false;

	@observable focusElement: string | null = null;
	@observable open = false;

	constructor(props: IContextMenuProps) {
		super(props);

		this.shouldInternalFocusUpdate(props);

		// tslint:disable-next-line: no-this-assignment
		const { hasActions, actions, hasChildren, children, type } = this;
		const { filterOptions } = props;

		let itemListLength = 0;

		if (type === EContextMenuTypes.actions && hasActions) {
			itemListLength = actions.length;
		} else if (type === EContextMenuTypes.children && hasChildren) {
			itemListLength = children.length;
		} else if (type === EContextMenuTypes.filter && filterOptions && filterOptions.filterMapping) {
			itemListLength = filterOptions.filterMapping.length;
			if (filterOptions && !filterOptions.hideClearFilters) {
				// Adding a additional item in the array for keyboard support to work with the clear button
				itemListLength++;
			}
		}

		this.itemReferences = new Array(itemListLength);

		makeObservable(this);
	}

	componentDidUpdate() {
		this.shouldInternalFocusUpdate(this.props);
	}

	shouldInternalFocusUpdate(props: IContextMenuProps) {
		const { focusedIndex } = props;
		if (focusedIndex !== undefined) {
			this.internalFocusedIndex = focusedIndex;
		}
	}

	@computed
	get filterMapping(): TContextMenuFilterMapping {
		const { filterOptions } = this.props;
		if (filterOptions && filterOptions.filterMapping) {
			return filterOptions.filterMapping.filter((mapping) => mapping.visible);
		}
		return [];
	}

	@computed
	get actions() {
		const { actions, payload } = this.props;
		if (actions && actions.length > 0) {
			return actions.filter((action) => !action.visible || action.visible(payload));
		}
		return [];
	}

	@computed
	get hasActions(): boolean {
		// tslint:disable-next-line: no-this-assignment
		const { actions } = this;
		return actions && actions.length > 0;
	}

	@computed
	get type(): TContextMenuTypes {
		const { type } = this.props;
		return type || 'children';
	}

	@computed
	get displayType(): TContextMenuDisplayTypes {
		return this.props?.displayType || EContextMenuDisplayTypes.dropdown;
	}

	@computed
	get filterSubMapping() {
		const { filterOptions } = this.props;
		if (this.filterMapping && this.focusElement) {
			const filterMapping = this.filterMapping.find(
				(mapping: IContextMenuFilterMappingItem) => mapping.key === this.focusElement,
			);
			if (filterMapping && filterMapping.subMapping) {
				const subMapping = filterMapping.subMapping.filter(
					(mapping: TContextMenuFilterMappingSubItem) => mapping.visible,
				);

				let itemListLength = subMapping.length;
				if (filterOptions && !filterOptions.hideClearFilters) {
					// Adding a additional item in the array for keyboard support to work with the clear button
					itemListLength++;
				}

				this.subItemReferences = new Array(itemListLength);

				return {
					subMapping,
					_parentKey: filterMapping.key,
				};
			}
		}
		return { subMapping: [] };
	}

	@computed
	get children() {
		const { children } = this.props;
		return Array.isArray(children) ? children : [children];
	}

	@computed
	get hasChildren(): boolean {
		// tslint:disable-next-line: no-this-assignment
		const { children } = this;
		return children && children.length > 0;
	}

	@computed
	get asset() {
		const asset = this.open ? 'base.contextMenu.dropdownIconExpanded' : 'base.contextMenu.dropdownIcon';
		return asset;
	}

	@computed
	get contextMenuLabel() {
		const { label, labelIcon, showPill, pillProps } = this.props;
		const overridingPillProps: IPillProps = Object.assign({}, pillProps, {
			value: label,
		});

		if (this.hasIconLabel) {
			return <Icon {...labelIcon} />;
		}
		if (showPill) {
			return <ContextMenuPill {...overridingPillProps} />;
		}
		if (typeof label === 'string') {
			return <Translate>{label}</Translate>;
		}
		return label;
	}

	@computed
	get hasIconLabel(): boolean {
		const { labelIcon } = this.props;
		return !common.commonUtils.isNullOrUndefined(labelIcon);
	}

	@computed
	get description() {
		const { description } = this.props;
		if (typeof description === 'string') {
			return <Translate>{description}</Translate>;
		}
		return description;
	}

	@computed
	get buttonProps() {
		const { describedById } = this.props;
		const props: Partial<IButtonProps & React.HTMLAttributes<HTMLElement>> = {
			className: this.hasIconLabel ? 'base-contextMenu-button-with-icon' : '',
			'aria-describedby': describedById,
			onClick: (event: THandleOnClickEvent) => {
				event.stopPropagation();
				this.toggleDropdown();
			},
			onKeyDown: (event: THandleKeyEvent) => this.handleKeyDown(event),
		};
		if (this.focusedButton) {
			props.isFocused = true;
			this.focusedButton = false;
		}
		return props;
	}

	assignItemReference = (reference: HTMLElement | null, index: number) => {
		if (reference && this.itemReferences && index >= 0 && index < this.itemReferences.length) {
			this.itemReferences[index] = reference;
		}
	};

	assignSubItemReference = (reference: HTMLElement | null, index: number) => {
		if (reference && this.subItemReferences && index >= 0 && index < this.subItemReferences.length) {
			this.subItemReferences[index] = reference;
		}
	};

	focusItemIndex = () => {
		if (this.itemReferences && this.itemReferences[this.internalFocusedIndex] !== undefined) {
			const reference = this.itemReferences[this.internalFocusedIndex];
			this.setSubItemDropdownTopMargin(reference);
			reference.focus();
		}
	};

	focusSubItemIndex = () => {
		if (this.subItemReferences && this.subItemReferences[this.internalFocusedSubIndex] !== undefined) {
			this.subItemReferences[this.internalFocusedSubIndex].focus();
		}
	};

	@action
	toggleDropdown = () => {
		this.open = !this.open;
		this.removeFocusElement();
		const { onToggleDropdown } = this.props;
		if (onToggleDropdown) {
			onToggleDropdown(this.open);
		}
		if (this.open) {
			this.forceInternalFocus = true;
		}
	};

	handleActionSelection = (action: IContextMenuAction) => {
		if (action) {
			const { payload } = this.props;
			const { onClick } = action;
			if (onClick) {
				onClick(payload);
			}
		}
	};

	handleChildSelection = (event: THandleOnClickEvent, itemIndex: number) => {
		const { onChildSelection } = this.props;
		if (onChildSelection) {
			onChildSelection(event, itemIndex);
		}
	};

	@action
	handleSelection = (
		event: THandleOnClickEvent,
		itemIndex?: number,
		action?: IContextMenuAction,
		mappingKey?: string,
		mappingSubKey?: string,
	) => {
		event.stopPropagation();
		event.preventDefault();
		const { isMultiSelect, filterOptions } = this.props;

		if (this.activeMenuType === 'menu') {
			if (itemIndex !== undefined && this.internalFocusedIndex !== itemIndex) {
				this.internalFocusedIndex = itemIndex;
				this.forceInternalFocus = true;
			}
		}

		if (this.activeMenuType === 'sub-menu') {
			if (itemIndex !== undefined && this.internalFocusedSubIndex !== itemIndex) {
				this.internalFocusedSubIndex = itemIndex;
				this.forceInternalFocus = true;
			}
		}

		if (action) {
			this.handleActionSelection(action);
		} else if (this.type === 'filter' && mappingKey && filterOptions) {
			filterOptions.toggleFilterCheckbox(mappingKey, mappingSubKey);
		} else if (itemIndex !== undefined) {
			this.handleChildSelection(event, itemIndex);
		}

		if (!isMultiSelect) {
			this.open = false;
			this.focusedButton = true;
		}
	};

	handleLeftRightNavigation = (key: string) => {
		if (key === KEYS.RIGHT_ARROW && this.activeMenuType === 'menu' && this.filterSubMapping) {
			this.activeMenuType = 'sub-menu';
		}
		if (key === KEYS.LEFT_ARROW && this.activeMenuType === 'sub-menu') {
			this.activeMenuType = 'menu';
			this.internalFocusedSubIndex = 0;
		}
	};

	onOpenNavigation = (key: string) => {
		const { pageKeyTraverseSize = 3 } = this.props;

		// leftArrow and rightArrow navigation to switch between menu or sub-menu
		this.handleLeftRightNavigation(key);

		const itemReferenceLength = this.itemReferences.length - 1;
		const subItemReferenceLength = this.subItemReferences.length - 1;

		const isSubMenuType = this.activeMenuType === 'sub-menu';
		const lastIndex = isSubMenuType ? subItemReferenceLength : itemReferenceLength;
		let nextIndex = isSubMenuType ? this.internalFocusedSubIndex : this.internalFocusedIndex;

		switch (key) {
			case KEYS.UP_ARROW:
				nextIndex -= 1;
				break;
			case KEYS.DOWN_ARROW:
				nextIndex += 1;
				break;
			case KEYS.PAGE_UP: {
				const pageUp = nextIndex - pageKeyTraverseSize;
				nextIndex = nextIndex > 0 && pageUp < 0 ? 0 : pageUp;
				break;
			}
			case KEYS.PAGE_DOWN: {
				const pageDown = nextIndex + pageKeyTraverseSize;
				nextIndex = nextIndex < lastIndex && pageDown > lastIndex ? 0 : pageDown;
				break;
			}
			case KEYS.HOME:
				nextIndex = 0;
				break;
			case KEYS.END:
				nextIndex = lastIndex;
				break;
			default:
				break;
		}
		if (nextIndex < 0) {
			nextIndex = lastIndex;
		}
		if (nextIndex > lastIndex) {
			nextIndex = 0;
		}

		if (isSubMenuType) {
			this.internalFocusedSubIndex = nextIndex;
			this.focusSubItemIndex();
		} else {
			this.internalFocusedIndex = nextIndex;
			this.focusItemIndex();
		}
	};

	onClosedNavigation = (key: string) => {
		switch (key) {
			case KEYS.UP_ARROW:
			case KEYS.DOWN_ARROW:
				this.toggleDropdown();
				break;
			default:
				break;
		}
	};

	handleKeyUp = (event: THandleKeyEvent, key: string, index: number) => {
		this.setFocusElement(key, index);
	};

	handleKeyDown = (
		event: THandleKeyEvent,
		itemIndex?: number,
		action?: IContextMenuAction,
		mappingKey?: string,
		mappingSubKey?: string,
	) => {
		const { onKeyDown } = this.props;
		if (onKeyDown) {
			onKeyDown(event, true);
		} else {
			const { key } = event;
			const isNavEvent = NAVIGATION_KEYS.indexOf(key as KEYS) !== -1;
			const isAcceptEvent = ACCEPT_KEYS.indexOf(key as KEYS) !== -1;
			const isExitEvent = EXIT_KEYS.indexOf(key as KEYS) !== -1;

			if (key !== KEYS.TAB && (isNavEvent || isAcceptEvent || isExitEvent)) {
				event.preventDefault();
				if (isNavEvent) {
					event.stopPropagation();
				}
			}

			if (this.open) {
				if (key === KEYS.TAB) {
					// Leave Commented to allow tab key within context menu so that the item can navigate grouped content
					// this.toggleDropdown();
				} else if (isNavEvent) {
					this.onOpenNavigation(key);
				} else if (isExitEvent) {
					this.toggleDropdown();
					this.focusedButton = true;
				} else if (isAcceptEvent && this.itemReferences.length > 0 && this.internalFocusedIndex >= 0) {
					this.handleSelection(
						event as unknown as THandleOnClickEvent,
						itemIndex,
						action,
						mappingKey,
						mappingSubKey,
					);
				}
			} else {
				if (isNavEvent) {
					this.onClosedNavigation(key);
				} else if (isAcceptEvent) {
					this.toggleDropdown();
				}
			}
		}
	};

	appendIsFocus = (props: Partial<IDropdownItemProps>, itemIndex: number): Partial<IDropdownItemProps> => {
		// tslint:disable-next-line: no-this-assignment
		const { forceInternalFocus, internalFocusedIndex } = this;
		const { focusedIndex: focusedIndexFromProps } = this.props;

		const isPropFocused = focusedIndexFromProps === itemIndex;
		const isInternallyFocused =
			focusedIndexFromProps === undefined && forceInternalFocus && internalFocusedIndex === itemIndex;
		if (isPropFocused || isInternallyFocused) {
			props.isFocused = true;
			this.forceInternalFocus = false;
		}

		return props;
	};

	@action
	setFocusElement = (key: string, index: number) => {
		const reference = this.itemReferences[index];
		this.setSubItemDropdownTopMargin(reference);
		this.focusElement = key;
	};

	@action
	removeFocusElement = () => {
		this.focusElement = null;
	};

	setSubItemDropdownTopMargin = (reference: HTMLElement | null) => {
		if (reference) {
			this.subItemDropdownTopMargin = reference.offsetTop || 0;
		}
	};

	render() {
		const { className, hideAsset, iconAlt, filterOptions } = this.props;
		const isInline = this.displayType === EContextMenuDisplayTypes.inline;
		// Type `children` and `actions` are left here for backwards compatibility
		const isChildren = this.type === EContextMenuTypes.children;
		const isActions = this.type === EContextMenuTypes.actions;
		const isFilter = this.type === EContextMenuTypes.filter;

		let items = null;
		let subItems = null;

		if (isChildren) {
			items = (
				<>
					{this.hasChildren &&
						this.children.map((item: ReactNode, index: number) => {
							const itemIsString = typeof item === 'string';
							const content = itemIsString ? <Translate>{item}</Translate> : item;
							const dropdownProps = this.appendIsFocus({}, index);

							return (
								<DropdownItem
									id={`${index}`}
									key={`${index}`}
									onClick={(event: THandleOnClickEvent) => {
										// The code below is added to check if the content variable is an anchor tag
										// If it is indeed an anchor tag, we don't have to run handleSelection method
										if (React.isValidElement(content)) {
											const { props } = content;
											if (props && (props as any).href) {
												return;
											}
										}
										this.handleSelection(event, index);
									}}
									onKeyDown={(event: THandleKeyEvent) => this.handleKeyDown(event, index)}
									getItemReference={(reference: HTMLElement | null) =>
										this.assignItemReference(reference, index)
									}
									{...dropdownProps}
								>
									{content}
								</DropdownItem>
							);
						})}
				</>
			);
		}

		if (isActions) {
			items = (
				<>
					{this.actions.map((action: IContextMenuAction, index: number) => {
						const { key, label } = action;
						const dropdownProps = this.appendIsFocus({}, index);

						return (
							<DropdownItem
								id={key || `${index}`}
								key={key || `${index}`}
								onClick={(event: THandleOnClickEvent) => {
									this.handleSelection(event, index, action);
								}}
								onKeyDown={(event: THandleKeyEvent) => this.handleKeyDown(event, index, action)}
								getItemReference={(reference: HTMLElement) =>
									this.assignItemReference(reference, index)
								}
								{...dropdownProps}
							>
								<ActionLabel>
									<Translate>{label}</Translate>
								</ActionLabel>
							</DropdownItem>
						);
					})}
				</>
			);
		}

		if (isFilter) {
			// Destructuring here to set a default on subMapping, it was causing the build to break
			const { subMapping = [], _parentKey } = this.filterSubMapping;

			const hideClearFilters = filterOptions && filterOptions.hideClearFilters;
			const onClearFilters = filterOptions && filterOptions.onClearFilters;
			const filterMappingLength = String(this.filterMapping.length);
			const filterSubMappingLength = String(this.filterSubMapping.subMapping.length);
			items = (
				<>
					{this.filterMapping.map(
						({ key, label, subMapping, checked, count }: IContextMenuFilterMappingItem, index: number) => {
							const dropdownProps = this.appendIsFocus({}, index);

							return (
								<DropdownItem
									id={key || `${index}`}
									key={key || `${index}`}
									onClick={(event: THandleOnClickEvent) => {
										this.handleSelection(event, index, undefined, String(key));
									}}
									onKeyDown={(event: THandleKeyEvent) =>
										this.handleKeyDown(event, index, undefined, String(key))
									}
									onKeyUp={(event: THandleKeyEvent) => this.handleKeyUp(event, key, index)}
									onMouseEnter={() => this.setFocusElement(key, index)}
									getItemReference={(reference: HTMLElement) =>
										this.assignItemReference(reference, index)
									}
									{...dropdownProps}
								>
									<Translate>
										<StyledLabel key={key}>
											<Checkbox value={checked} />
											{label}
										</StyledLabel>
										<Count>{count && ` (${count})`}</Count>
										{!isInline && (
											<IconWrapper>
												{subMapping && subMapping.length > 0 && (
													<Icon
														alt={'Context Sub-Menu Icon'}
														asset="base.paging.rightArrow"
													/>
												)}
											</IconWrapper>
										)}
									</Translate>
								</DropdownItem>
							);
						},
					)}
					{!hideClearFilters && (
						<DropdownItem
							id={filterMappingLength}
							key={filterMappingLength}
							onClick={() => {
								onClearFilters && onClearFilters();
							}}
							onKeyDown={(event: THandleKeyEvent) =>
								this.handleKeyDown(event, Number(filterMappingLength))
							}
							onKeyUp={(event: THandleKeyEvent) =>
								this.handleKeyUp(event, filterMappingLength, Number(filterMappingLength))
							}
							onMouseEnter={() => this.setFocusElement(filterMappingLength, Number(filterMappingLength))}
							getItemReference={(reference: HTMLElement) =>
								this.assignItemReference(reference, Number(filterMappingLength))
							}
						>
							<Translate>
								<ClearButton>Clear</ClearButton>
							</Translate>
						</DropdownItem>
					)}
				</>
			);

			if (subMapping && subMapping.length > 0) {
				subItems = (
					<>
						{subMapping.map(
							({ key, label, checked, count }: TContextMenuFilterMappingSubItem, index: number) => {
								const dropdownProps = this.appendIsFocus({}, index);

								const args = [index, undefined, String(_parentKey), String(key)];
								return (
									<DropdownItem
										id={key || `${index}`}
										key={key || `${index}`}
										onClick={(event: THandleOnClickEvent) => {
											this.handleSelection(event, ...(args as any));
										}}
										onKeyDown={(event: THandleKeyEvent) =>
											this.handleKeyDown(event, ...(args as any))
										}
										getItemReference={(reference: HTMLElement) =>
											this.assignSubItemReference(reference, index)
										}
										{...dropdownProps}
									>
										<Translate>
											<StyledLabel key={key}>
												<Checkbox value={checked} />
												{label}
											</StyledLabel>
											<Count>{count && ` (${count})`}</Count>
										</Translate>
									</DropdownItem>
								);
							},
						)}
						{!hideClearFilters && (
							<DropdownItem
								id={filterMappingLength}
								key={filterMappingLength}
								onClick={() => {
									onClearFilters && onClearFilters();
								}}
								onKeyDown={(event: THandleKeyEvent) =>
									this.handleKeyDown(event, Number(filterSubMappingLength))
								}
								getItemReference={(reference: HTMLElement) =>
									this.assignSubItemReference(reference, Number(filterSubMappingLength))
								}
							>
								<Translate>
									<ClearButton>Clear</ClearButton>
								</Translate>
							</DropdownItem>
						)}
					</>
				);
			}
		}

		return (
			<Wrapper className={className}>
				{!isInline && (
					<Dropdown
						buttonProps={this.buttonProps}
						contextMenuLabel={this.contextMenuLabel}
						asset={this.asset}
						toggleDropdown={this.toggleDropdown}
						open={this.open}
						iconAlt={iconAlt}
						hideAsset={hideAsset}
					>
						<DropdownContent>
							<DropdownItemWrapper role="list">{items}</DropdownItemWrapper>
						</DropdownContent>
						{subItems !== null && (
							<DropdownContent
								style={{
									marginTop: `${this.subItemDropdownTopMargin}px`,
								}}
							>
								<DropdownItemWrapper role="list">{subItems}</DropdownItemWrapper>
							</DropdownContent>
						)}
					</Dropdown>
				)}
				{isInline && (
					<>
						<InlineLabel>{this.contextMenuLabel}</InlineLabel>
						<InlineDescription>{this.description}</InlineDescription>
						<InlineItemWrapper role="list">{items}</InlineItemWrapper>
					</>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(ContextMenu), CONTEXT_MENU_CATEGORY_KEY, CONTEXT_MENU_COMPONENT_KEY);
